import React, { useState, useMemo } from 'react';
import {
  Search,
  Bell,
  CheckCircle2,
  AlertTriangle,
  Activity,
  Layers,
  Lock,
  Network,
  FileText,
  RefreshCw,
  Users,
  TrendingUp,
  DollarSign,
  Database,
  ShieldAlert,
  Sliders,
  HelpCircle,
  ChevronRight,
  Plus,
  ShieldCheck,
  Check,
  ExternalLink,
  Clock,
  Filter,
  X,
  Building2,
  Globe,
  Key,
  CreditCard,
  LifeBuoy,
  History,
  FolderGit2,
  Zap,
  MoreHorizontal,
  Download,
  Mail,
  ChevronDown,
  UserPlus,
  Trash2,
  Power,
  RotateCcw,
  CheckCircle,
  AlertCircle,
  Radio,
  SlidersHorizontal,
  ChevronLeft,
  Shield,
  UserCheck,
  Settings,
  Edit3,
  Eye,
  KeyRound,
  Fingerprint,
  Info,
  Server,
  Terminal,
  Cpu,
  FileCheck,
  Workflow,
  Sparkles
} from 'lucide-react';
import { AppView } from '../../types';

interface RolesPermissionsManagementProps {
  onNavigateToView: (view: AppView) => void;
  onOpenSearch?: () => void;
}

export type PermissionNamespace =
  | 'users'
  | 'support'
  | 'product'
  | 'finance'
  | 'data'
  | 'ai'
  | 'operations'
  | 'compliance'
  | 'configuration';

export interface PermissionItem {
  key: string;
  name: string;
  description: string;
  namespace: PermissionNamespace;
  isHighRisk?: boolean;
}

export interface RoleUser {
  id: string;
  name: string;
  email: string;
  department: string;
  mfaEnabled: boolean;
  assignedDate: string;
  assignedBy: string;
  avatarInitials: string;
}

export interface RoleAuditRecord {
  id: string;
  timestamp: string;
  actor: string;
  actorEmail: string;
  action: string;
  details: string;
  ipAddress: string;
  stepUpMethod: string;
}

export interface SystemRole {
  id: string;
  name: string;
  slug: string;
  description: string;
  type: 'System Default' | 'Custom Role' | 'Administrative';
  status: 'Active' | 'Locked' | 'Draft';
  scope: 'Global Platform' | 'Regional Workspace' | 'Domain Limited';
  isSystemProtected: boolean;
  usersCount: number;
  lastModified: string;
  modifiedBy: string;
  permissions: string[]; // List of permission keys like 'users.read', 'finance.invoices.read', etc.
  assignedUsers: RoleUser[];
  auditHistory: RoleAuditRecord[];
}

// Canonical Backend Permission Catalog
export const PERMISSION_CATALOG: { namespace: PermissionNamespace; label: string; icon: any; permissions: PermissionItem[] }[] = [
  {
    namespace: 'users',
    label: 'Users & Organizations (users.*)',
    icon: Users,
    permissions: [
      { key: 'users.read', name: 'View Organizations & Users', description: 'Read-only access to customer workspaces, membership rosters, and user profiles.', namespace: 'users' },
      { key: 'users.manage', name: 'Create & Update Organizations', description: 'Provision customer workspaces, update organization metadata, and modify domains.', namespace: 'users' },
      { key: 'users.suspend', name: 'Suspend & Terminate Workspaces', description: 'High-impact power to freeze customer access or revoke organization standing.', namespace: 'users', isHighRisk: true },
      { key: 'users.mfa_reset', name: 'Reset User MFA / Security Factor', description: 'Emergency breakglass to reset lost 2FA tokens for workspace administrators.', namespace: 'users', isHighRisk: true }
    ]
  },
  {
    namespace: 'support',
    label: 'Customer Support & Experience (support.*)',
    icon: LifeBuoy,
    permissions: [
      { key: 'support.tickets.read', name: 'View Customer Tickets', description: 'Access support desk, inquiries, and customer communication threads.', namespace: 'support' },
      { key: 'support.tickets.reply', name: 'Respond & Resolve Tickets', description: 'Send official support messages, assign priority, and close tickets.', namespace: 'support' },
      { key: 'support.sla.escalate', name: 'Escalate to Engineering / TAM', description: 'Trigger 24/7 incident response for P1 SLA breach prevention.', namespace: 'support' }
    ]
  },
  {
    namespace: 'product',
    label: 'Product & Catalog (product.*)',
    icon: TrendingUp,
    permissions: [
      { key: 'product.catalog.read', name: 'View Product Specifications', description: 'Inspect calculation engine feature specs, APIs, and product roadmaps.', namespace: 'product' },
      { key: 'product.features.manage', name: 'Manage Feature Rollouts', description: 'Configure feature availability, beta programs, and version releases.', namespace: 'product' },
      { key: 'product.experiments.control', name: 'Launch & Tweak A/B Tests', description: 'Modify live experimentation weighting and audience assignments.', namespace: 'product', isHighRisk: true }
    ]
  },
  {
    namespace: 'finance',
    label: 'Finance & Invoicing (finance.*)',
    icon: DollarSign,
    permissions: [
      { key: 'finance.invoices.read', name: 'View Platform Invoices & Ledgers', description: 'Audit billing statements, enterprise license agreements, and receipts.', namespace: 'finance' },
      { key: 'finance.invoices.generate', name: 'Generate & Adjust Invoices', description: 'Issue custom enterprise invoices, credit notes, and tax corrections.', namespace: 'finance' },
      { key: 'finance.subscriptions.modify', name: 'Change Enterprise Contract Terms', description: 'Update annual commitment fees, tier limits, and billing schedules.', namespace: 'finance', isHighRisk: true }
    ]
  },
  {
    namespace: 'data',
    label: 'Data & Telemetry (data.*)',
    icon: Database,
    permissions: [
      { key: 'data.pipelines.view', name: 'View Pipeline Telemetry', description: 'Observe ingestion latency, worker queue volume, and warehouse syncs.', namespace: 'data' },
      { key: 'data.export.pii_masked', name: 'Export Anonymized Datasets', description: 'Generate aggregated data analytics exports with strict PII masking.', namespace: 'data' },
      { key: 'data.retention.configure', name: 'Configure Data Retention Policies', description: 'Modify retention windows for audit logs, webhooks, and raw payloads.', namespace: 'data', isHighRisk: true }
    ]
  },
  {
    namespace: 'ai',
    label: 'AI & Gateway Intelligence (ai.*)',
    icon: Sparkles,
    permissions: [
      { key: 'ai.telemetry.view', name: 'View AI Gateway Latency & Usage', description: 'Monitor token consumption, cache hit rates, and model error rates.', namespace: 'ai' },
      { key: 'ai.prompts.manage', name: 'Update System Prompts & Guardrails', description: 'Modify underlying prompt templates, temperature, and safety thresholds.', namespace: 'ai', isHighRisk: true },
      { key: 'ai.evals.run', name: 'Execute Evaluation Benchmarks', description: 'Run automated accuracy and compliance test suites on AI reasoning.', namespace: 'ai' }
    ]
  },
  {
    namespace: 'operations',
    label: 'Operations & Infrastructure (operations.*)',
    icon: ShieldAlert,
    permissions: [
      { key: 'operations.health.view', name: 'View System Health & Metrics', description: 'Real-time telemetry for CPU, DB pool, Redis queue, and microservices.', namespace: 'operations' },
      { key: 'operations.deployments.trigger', name: 'Trigger Deployments & Builds', description: 'Deploy application artifacts and container revisions to Staging/Prod.', namespace: 'operations', isHighRisk: true },
      { key: 'operations.rollback.execute', name: 'Execute Emergency Rollback', description: 'Instant traffic redirect to previous stable production revision.', namespace: 'operations', isHighRisk: true }
    ]
  },
  {
    namespace: 'compliance',
    label: 'Compliance & Audit (compliance.*)',
    icon: FileCheck,
    permissions: [
      { key: 'compliance.audit_logs.read', name: 'Inspect Global Audit Logs', description: 'Immutable access to administrative event logs, IP addresses, and actors.', namespace: 'compliance' },
      { key: 'compliance.export_soc2', name: 'Generate SOC 2 / ISO Evidence', description: 'Export cryptographic access reports for external security auditors.', namespace: 'compliance' },
      { key: 'compliance.privacy.export', name: 'Process GDPR/CCPA Subject Requests', description: 'Handle data deletion and portability requests with audit traceability.', namespace: 'compliance', isHighRisk: true }
    ]
  },
  {
    namespace: 'configuration',
    label: 'System Configuration (configuration.*)',
    icon: Sliders,
    permissions: [
      { key: 'configuration.flags.toggle', name: 'Toggle Dynamic Feature Flags', description: 'Turn platform engine capabilities and experimental features on/off.', namespace: 'configuration', isHighRisk: true },
      { key: 'configuration.roles.manage', name: 'Manage Roles & RBAC Matrix', description: 'Create, edit, and assign permission sets to staff and operators.', namespace: 'configuration', isHighRisk: true },
      { key: 'configuration.security.mfa_policy', name: 'Enforce Organization MFA Policies', description: 'Require WebAuthn/Hardware keys across admin console users.', namespace: 'configuration', isHighRisk: true }
    ]
  }
];

export const RolesPermissionsManagement: React.FC<RolesPermissionsManagementProps> = ({
  onNavigateToView,
  onOpenSearch
}) => {
  const [selectedDomain, setSelectedDomain] = useState<string>('configuration');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Selected Role for inspection / configuration
  const [selectedRoleId, setSelectedRoleId] = useState<string>('super_admin');
  
  // Tab within the detail / edit view
  const [activeDetailTab, setActiveDetailTab] = useState<'matrix' | 'users' | 'scope' | 'audit'>('matrix');

  // Edit Mode state
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [stagedPermissions, setStagedPermissions] = useState<string[]>([]);
  
  // High-Risk Step-Up Auth Modal State
  const [showStepUpModal, setShowStepUpModal] = useState<boolean>(false);
  const [stepUpPassword, setStepUpPassword] = useState<string>('');
  const [stepUpTotp, setStepUpTotp] = useState<string>('');
  const [stepUpReason, setStepUpReason] = useState<string>('');
  const [isSubmittingAuth, setIsSubmittingAuth] = useState<boolean>(false);

  // Create New Role Modal
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [newRoleName, setNewRoleName] = useState<string>('');
  const [newRoleDesc, setNewRoleDesc] = useState<string>('');
  const [newRoleScope, setNewRoleScope] = useState<'Global Platform' | 'Regional Workspace' | 'Domain Limited'>('Global Platform');
  const [newRoleBaseTemplate, setNewRoleBaseTemplate] = useState<string>('none');

  // Initial Real Roles
  const [roles, setRoles] = useState<SystemRole[]>([
    {
      id: 'super_admin',
      name: 'Super Admin',
      slug: 'super-admin',
      description: 'Full control center access to manage staff, customer workspaces, and platform configuration.',
      type: 'Administrative',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: true,
      usersCount: 3,
      lastModified: '2026-08-20',
      modifiedBy: 'SecOps Automated Baseline',
      permissions: [
        'users.read', 'users.manage', 'users.suspend', 'users.mfa_reset',
        'support.tickets.read', 'support.tickets.reply', 'support.sla.escalate',
        'product.catalog.read', 'product.features.manage', 'product.experiments.control',
        'finance.invoices.read', 'finance.invoices.generate', 'finance.subscriptions.modify',
        'data.pipelines.view', 'data.export.pii_masked', 'data.retention.configure',
        'ai.telemetry.view', 'ai.prompts.manage', 'ai.evals.run',
        'operations.health.view', 'operations.deployments.trigger', 'operations.rollback.execute',
        'compliance.audit_logs.read', 'compliance.export_soc2', 'compliance.privacy.export',
        'configuration.flags.toggle', 'configuration.roles.manage', 'configuration.security.mfa_policy'
      ],
      assignedUsers: [
        { id: 'u-1', name: 'Sarah Jenkins', email: 'sarah.j@yamoopay.com', department: 'Executive / IT', mfaEnabled: true, assignedDate: '2025-01-10', assignedBy: 'System Root', avatarInitials: 'SJ' },
        { id: 'u-2', name: 'Alexandre Mercier', email: 'alexandre.m@yamoopay.com', department: 'SecOps', mfaEnabled: true, assignedDate: '2025-02-15', assignedBy: 'System Root', avatarInitials: 'AM' },
        { id: 'u-3', name: 'Harry Chama', email: 'harryeliechama@gmail.com', department: 'Engineering Leadership', mfaEnabled: true, assignedDate: '2025-03-01', assignedBy: 'System Root', avatarInitials: 'HC' }
      ],
      auditHistory: [
        { id: 'aud-1', timestamp: '2026-08-20 14:10:22', actor: 'Alexandre Mercier', actorEmail: 'alexandre.m@yamoopay.com', action: 'Audited Super Admin membership list', details: 'Confirmed all 3 assigned users have WebAuthn hardware keys active.', ipAddress: '192.168.1.1', stepUpMethod: 'WebAuthn FIDO2' },
        { id: 'aud-2', timestamp: '2026-05-12 09:30:00', actor: 'System Initializer', actorEmail: 'root@yamoopay.internal', action: 'Provisioned Super Admin core role', details: 'Bound 28 control-plane permissions with strict non-database-superuser constraints.', ipAddress: '127.0.0.1', stepUpMethod: 'Genesis Provisioning' }
      ]
    },
    {
      id: 'support_admin',
      name: 'Support Admin',
      slug: 'support-admin',
      description: 'Customer ticket management, SLA escalation, and customer workspace diagnostics.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 8,
      lastModified: '2026-07-14',
      modifiedBy: 'Sarah Jenkins',
      permissions: [
        'users.read',
        'support.tickets.read', 'support.tickets.reply', 'support.sla.escalate',
        'product.catalog.read',
        'finance.invoices.read',
        'operations.health.view',
        'compliance.audit_logs.read'
      ],
      assignedUsers: [
        { id: 'u-11', name: 'Chloe Dupont', email: 'chloe.d@yamoopay.com', department: 'Customer Success', mfaEnabled: true, assignedDate: '2025-06-11', assignedBy: 'Sarah Jenkins', avatarInitials: 'CD' },
        { id: 'u-12', name: 'Marcus Vance', email: 'marcus.v@yamoopay.com', department: 'Technical Support Tier 2', mfaEnabled: true, assignedDate: '2025-07-20', assignedBy: 'Sarah Jenkins', avatarInitials: 'MV' }
      ],
      auditHistory: [
        { id: 'aud-11', timestamp: '2026-07-14 11:20:00', actor: 'Sarah Jenkins', actorEmail: 'sarah.j@yamoopay.com', action: 'Added users.read permission to Support Admin', details: 'Enabled workspace customer diagnostics without modification rights.', ipAddress: '192.168.1.45', stepUpMethod: 'TOTP Verified' }
      ]
    },
    {
      id: 'product_manager',
      name: 'Product Manager',
      slug: 'product-manager',
      description: 'Feature flag management, roadmap planning, beta rollouts, and telemetry analysis.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 6,
      lastModified: '2026-06-30',
      modifiedBy: 'Harry Chama',
      permissions: [
        'users.read',
        'product.catalog.read', 'product.features.manage', 'product.experiments.control',
        'data.pipelines.view', 'data.export.pii_masked',
        'ai.telemetry.view',
        'configuration.flags.toggle'
      ],
      assignedUsers: [
        { id: 'u-21', name: 'Elena Rostova', email: 'elena.r@yamoopay.com', department: 'Product Management', mfaEnabled: true, assignedDate: '2025-04-12', assignedBy: 'Harry Chama', avatarInitials: 'ER' }
      ],
      auditHistory: []
    },
    {
      id: 'finance_analyst',
      name: 'Finance Analyst',
      slug: 'finance-analyst',
      description: 'Platform billing inspection, enterprise licensing invoices, and financial ledger audits.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 4,
      lastModified: '2026-05-18',
      modifiedBy: 'Sarah Jenkins',
      permissions: [
        'users.read',
        'finance.invoices.read', 'finance.invoices.generate',
        'data.export.pii_masked',
        'compliance.audit_logs.read'
      ],
      assignedUsers: [
        { id: 'u-31', name: 'Markus Weber', email: 'markus.w@yamoopay.com', department: 'Corporate Finance', mfaEnabled: true, assignedDate: '2025-05-02', assignedBy: 'Sarah Jenkins', avatarInitials: 'MW' }
      ],
      auditHistory: []
    },
    {
      id: 'data_steward',
      name: 'Data Steward',
      slug: 'data-steward',
      description: 'Data catalog oversight, PII classification, anonymized export governance, and retention rules.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 3,
      lastModified: '2026-08-05',
      modifiedBy: 'Alexandre Mercier',
      permissions: [
        'data.pipelines.view', 'data.export.pii_masked', 'data.retention.configure',
        'compliance.audit_logs.read', 'compliance.privacy.export'
      ],
      assignedUsers: [
        { id: 'u-41', name: 'Priya Patel', email: 'priya.p@yamoopay.com', department: 'Data Governance', mfaEnabled: true, assignedDate: '2025-08-10', assignedBy: 'Alexandre Mercier', avatarInitials: 'PP' }
      ],
      auditHistory: []
    },
    {
      id: 'ai_operator',
      name: 'AI Operator',
      slug: 'ai-operator',
      description: 'AI Gateway monitoring, prompt template testing, accuracy benchmarks, and safety thresholds.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 5,
      lastModified: '2026-08-22',
      modifiedBy: 'Harry Chama',
      permissions: [
        'ai.telemetry.view', 'ai.prompts.manage', 'ai.evals.run',
        'data.pipelines.view',
        'operations.health.view'
      ],
      assignedUsers: [
        { id: 'u-51', name: 'David Vance', email: 'david.v@yamoopay.com', department: 'AI & Machine Learning', mfaEnabled: true, assignedDate: '2026-01-15', assignedBy: 'Harry Chama', avatarInitials: 'DV' }
      ],
      auditHistory: []
    },
    {
      id: 'security_analyst',
      name: 'Security Analyst',
      slug: 'security-analyst',
      description: 'Security incident triage, MFA enforcement audit, TLS monitoring, and threat analysis.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 7,
      lastModified: '2026-08-11',
      modifiedBy: 'Alexandre Mercier',
      permissions: [
        'users.read', 'users.mfa_reset',
        'operations.health.view',
        'compliance.audit_logs.read', 'compliance.export_soc2',
        'configuration.security.mfa_policy'
      ],
      assignedUsers: [
        { id: 'u-61', name: 'Liam O\'Connor', email: 'liam.o@yamoopay.com', department: 'Information Security', mfaEnabled: true, assignedDate: '2025-09-01', assignedBy: 'Alexandre Mercier', avatarInitials: 'LO' }
      ],
      auditHistory: []
    },
    {
      id: 'compliance_reviewer',
      name: 'Compliance Reviewer',
      slug: 'compliance-reviewer',
      description: 'Read-only access to compliance reports, immutable audit logs, and privacy fulfillment records.',
      type: 'System Default',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 12,
      lastModified: '2026-07-02',
      modifiedBy: 'Sarah Jenkins',
      permissions: [
        'compliance.audit_logs.read', 'compliance.export_soc2', 'compliance.privacy.export',
        'users.read',
        'finance.invoices.read'
      ],
      assignedUsers: [
        { id: 'u-71', name: 'Jean-Paul Moreau', email: 'jp.moreau@yamoopay.com', department: 'Legal & Compliance', mfaEnabled: true, assignedDate: '2025-10-18', assignedBy: 'Sarah Jenkins', avatarInitials: 'JM' }
      ],
      auditHistory: []
    },
    {
      id: 'developer_admin',
      name: 'Developer Admin',
      slug: 'developer-admin',
      description: 'Developer portal maintenance, sandbox testing, webhook debugging, and API release coordination.',
      type: 'Administrative',
      status: 'Active',
      scope: 'Global Platform',
      isSystemProtected: false,
      usersCount: 9,
      lastModified: '2026-08-19',
      modifiedBy: 'Harry Chama',
      permissions: [
        'users.read',
        'product.catalog.read', 'product.features.manage',
        'ai.telemetry.view',
        'operations.health.view', 'operations.deployments.trigger',
        'configuration.flags.toggle'
      ],
      assignedUsers: [
        { id: 'u-81', name: 'Kenji Sato', email: 'kenji.s@yamoopay.com', department: 'Developer Experience', mfaEnabled: true, assignedDate: '2026-02-20', assignedBy: 'Harry Chama', avatarInitials: 'KS' }
      ],
      auditHistory: []
    }
  ]);

  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Currently selected role object
  const activeRole = useMemo(() => {
    return roles.find((r) => r.id === selectedRoleId) || roles[0];
  }, [roles, selectedRoleId]);

  // Filtered Roles List
  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles;
    const q = searchQuery.toLowerCase();
    return roles.filter((r) => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || r.slug.includes(q));
  }, [roles, searchQuery]);

  // Handle Edit Mode Toggle
  const handleStartEdit = () => {
    setStagedPermissions([...activeRole.permissions]);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setStagedPermissions([]);
  };

  const handleTogglePermission = (permKey: string) => {
    if (!isEditMode) return;
    if (stagedPermissions.includes(permKey)) {
      setStagedPermissions(stagedPermissions.filter((k) => k !== permKey));
    } else {
      setStagedPermissions([...stagedPermissions, permKey]);
    }
  };

  // High-Risk Permission Calculation
  const highRiskChanges = useMemo(() => {
    if (!isEditMode) return [];
    const added = stagedPermissions.filter((k) => !activeRole.permissions.includes(k));
    const removed = activeRole.permissions.filter((k) => !stagedPermissions.includes(k));
    
    // Find all catalog items
    const allPerms = PERMISSION_CATALOG.flatMap((c) => c.permissions);
    const addedItems = allPerms.filter((p) => added.includes(p.key));
    const removedItems = allPerms.filter((p) => removed.includes(p.key));

    return {
      added,
      removed,
      addedItems,
      removedItems,
      hasHighRisk: [...addedItems, ...removedItems].some((p) => p.isHighRisk)
    };
  }, [isEditMode, stagedPermissions, activeRole]);

  // Save Staged Changes (Triggers Step-Up Auth if High Risk or Protected Role)
  const handleInitiateSave = () => {
    // If high risk or modifying Super Admin / Admin role, require step-up auth
    if (activeRole.isSystemProtected || highRiskChanges.hasHighRisk || activeRole.type === 'Administrative') {
      setShowStepUpModal(true);
      return;
    }

    // Otherwise apply directly with server confirmation
    applyPermissionChanges('Standard Session Token');
  };

  const applyPermissionChanges = (stepUpMethod: string) => {
    const updatedRoles = roles.map((r) => {
      if (r.id === activeRole.id) {
        const newAudit: RoleAuditRecord = {
          id: `aud-${Date.now()}`,
          timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
          actor: 'Admin User (You)',
          actorEmail: 'admin@yamoopay.com',
          action: 'Updated granular permission matrix',
          details: `Modified permissions. Net change: +${highRiskChanges.added.length} / -${highRiskChanges.removed.length} scopes.`,
          ipAddress: '192.168.1.100',
          stepUpMethod
        };

        return {
          ...r,
          permissions: stagedPermissions,
          lastModified: new Date().toISOString().split('T')[0],
          modifiedBy: 'Admin User (Control Center)',
          auditHistory: [newAudit, ...r.auditHistory]
        };
      }
      return r;
    });

    setRoles(updatedRoles);
    setIsEditMode(false);
    setShowStepUpModal(false);
    setStepUpPassword('');
    setStepUpTotp('');
    setStepUpReason('');
    triggerToast(`Role "${activeRole.name}" permissions updated & server-enforced successfully!`);
  };

  const handleConfirmStepUp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!stepUpPassword.trim() && !stepUpTotp.trim()) {
      triggerToast('Please provide your admin confirmation credential.');
      return;
    }
    setIsSubmittingAuth(true);

    setTimeout(() => {
      setIsSubmittingAuth(false);
      applyPermissionChanges('Hardware WebAuthn / TOTP Step-Up');
    }, 600);
  };

  // Create New Role
  const handleCreateRole = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName.trim()) {
      triggerToast('Please provide a role name.');
      return;
    }

    let initialPerms: string[] = ['users.read'];
    if (newRoleBaseTemplate !== 'none') {
      const template = roles.find((r) => r.id === newRoleBaseTemplate);
      if (template) initialPerms = [...template.permissions];
    }

    const roleId = `role_${newRoleName.toLowerCase().replace(/[^a-z0-9]/g, '_').slice(0, 16)}_${Math.floor(100 + Math.random() * 900)}`;
    const newRoleObj: SystemRole = {
      id: roleId,
      name: newRoleName.trim(),
      slug: newRoleName.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      description: newRoleDesc.trim() || 'Custom organizational permission profile.',
      type: 'Custom Role',
      status: 'Active',
      scope: newRoleScope,
      isSystemProtected: false,
      usersCount: 0,
      lastModified: new Date().toISOString().split('T')[0],
      modifiedBy: 'Admin User',
      permissions: initialPerms,
      assignedUsers: [],
      auditHistory: [
        {
          id: `aud-${Date.now()}`,
          timestamp: new Date().toISOString().replace('T', ' ').substring(0, 19),
          actor: 'Admin User',
          actorEmail: 'admin@yamoopay.com',
          action: 'Created new custom role profile',
          details: `Provisioned with ${initialPerms.length} initial permissions based on template "${newRoleBaseTemplate}".`,
          ipAddress: '192.168.1.100',
          stepUpMethod: 'Session Token'
        }
      ]
    };

    setRoles([...roles, newRoleObj]);
    setSelectedRoleId(newRoleObj.id);
    setShowCreateModal(false);
    setNewRoleName('');
    setNewRoleDesc('');
    setNewRoleBaseTemplate('none');
    triggerToast(`Role "${newRoleObj.name}" created successfully!`);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex text-[#1E293B] font-sans antialiased">
      {/* =============================================================== */}
      {/* 1. LEFT SIDEBAR (Canonical 7 Domains)                           */}
      {/* =============================================================== */}
      <aside className="w-64 bg-white border-r border-[#E2E8F0] flex flex-col shrink-0 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Brand Header */}
        <div className="p-5 border-b border-[#F1F5F9] flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0F172A] flex items-center justify-center text-white shadow-xs font-black text-lg">
            <span className="font-black text-white text-base leading-none">Y</span>
          </div>
          <div>
            <h1 className="text-base font-extrabold text-[#0F172A] tracking-tight leading-none">
              Control Center
            </h1>
            <span className="text-[10px] font-bold text-[#64748B] uppercase tracking-wider block mt-1">
              Admin Console
            </span>
          </div>
        </div>

        {/* Sidebar Navigation Links */}
        <div className="flex-1 py-4 px-3 space-y-6">
          {/* Section: CORE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              CORE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('overview');
                  onNavigateToView('admin-super-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'overview'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Layers className="w-4 h-4" />
                <span>Overview</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('customers');
                  onNavigateToView('admin-organizations');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'customers'
                    ? 'bg-[#2563EB] text-white font-bold shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Customers & Support</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('product');
                  onNavigateToView('cpo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'product'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                <span>Product & Growth</span>
              </button>
            </nav>
          </div>

          {/* Section: PERFORMANCE */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              PERFORMANCE
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('revenue');
                  onNavigateToView('cfo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'revenue'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                <span>Revenue & Finance</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('data');
                  onNavigateToView('cdo-command-center');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'data'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Database className="w-4 h-4" />
                <span>Data & AI</span>
              </button>
            </nav>
          </div>

          {/* Section: SYSTEM */}
          <div>
            <div className="px-3 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-widest">
              SYSTEM
            </div>
            <nav className="space-y-1">
              <button
                onClick={() => {
                  setSelectedDomain('operations');
                  onNavigateToView('admin-system-health');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                  selectedDomain === 'operations'
                    ? 'bg-[#2563EB] text-white font-bold'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <ShieldAlert className="w-4 h-4" />
                <span>Operations & Risk</span>
              </button>

              <button
                onClick={() => {
                  setSelectedDomain('configuration');
                }}
                className={`w-[#95%] flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedDomain === 'configuration'
                    ? 'bg-[#2563EB] text-white shadow-xs'
                    : 'text-[#475569] hover:bg-[#F1F5F9] hover:text-[#0F172A]'
                }`}
              >
                <Sliders className="w-4 h-4" />
                <span>Configuration</span>
              </button>

              {/* Sub-navigation under Configuration */}
              {selectedDomain === 'configuration' && (
                <div className="ml-4 pl-3 border-l-2 border-[#2563EB] my-1 space-y-1">
                  <button
                    onClick={() => {}}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold text-[#2563EB] bg-blue-50 transition-colors cursor-pointer"
                  >
                    Roles & Permissions
                  </button>
                  <button
                    onClick={() => onNavigateToView('admin-feature-flags')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    Feature Flags
                  </button>
                  <button
                    onClick={() => onNavigateToView('admin-audit')}
                    className="w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium text-[#64748B] hover:text-[#0F172A] transition-colors cursor-pointer"
                  >
                    System Audit Logs
                  </button>
                </div>
              )}
            </nav>
          </div>
        </div>

        {/* Sidebar Footer Profile */}
        <div className="p-3 border-t border-[#F1F5F9]">
          <div className="flex items-center gap-3 p-2 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-emerald-400 font-bold text-xs flex items-center justify-center border border-emerald-500/30">
              AU
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-[#0F172A] block truncate">Admin User</span>
              <span className="text-[10px] font-mono text-[#64748B] block truncate">Super Admin</span>
            </div>
          </div>
        </div>
      </aside>

      {/* =============================================================== */}
      {/* 2. MAIN CONTENT AREA                                           */}
      {/* =============================================================== */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* TOPBAR */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] px-8 flex items-center justify-between sticky top-0 z-20 shadow-2xs">
          {/* Breadcrumb Header in topbar (matches Stitch reference) */}
          <div className="flex items-center gap-2 text-xs text-[#64748B] font-medium">
            <span>Configuration</span>
            <ChevronRight className="w-3 h-3 text-[#CBD5E1]" />
            <span className="font-bold text-[#0F172A]">Roles & Permissions</span>
          </div>

          {/* Search Input in topbar */}
          <div className="flex items-center gap-4 flex-1 max-w-md mx-6">
            <div className="relative w-full">
              <Search className="w-4 h-4 text-[#94A3B8] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search roles, descriptions, or permissions..."
                className="w-full pl-9 pr-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] placeholder-[#94A3B8] focus:bg-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/10 outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#0F172A]"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Topbar Utility Icons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => triggerToast('RBAC Gateway: Enforced via Server JWT & Zero Trust Claims')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Server Enforcement Status"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </button>
            <button
              onClick={() => triggerToast('Security Audit: All administrative role changes require MFA step-up')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
            </button>
            <button
              onClick={() => onNavigateToView('help-center')}
              className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-xl transition-colors cursor-pointer"
              title="Help & Documentation"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-500 text-white font-bold text-xs flex items-center justify-center shadow-xs">
              AD
            </div>
          </div>
        </header>

        {/* PAGE BODY */}
        <main className="p-8 space-y-6 max-w-7xl mx-auto w-full">
          {/* Toast Notification */}
          {toastMessage && (
            <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] text-white text-xs font-bold px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-in slide-in-from-bottom-5 duration-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>{toastMessage}</span>
            </div>
          )}

          {/* Title and Primary Action Row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">
                Roles & Permissions
              </h1>
              <p className="text-xs text-[#64748B] font-medium mt-1">
                Manage system access levels, granular permission sets, and server-side RBAC enforcement.
              </p>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4 text-white" />
              <span>Create New Role</span>
            </button>
          </div>

          {/* CRITICAL SECURITY BANNER: Super Admin ≠ Database Superuser */}
          <div className="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-4 flex items-start gap-3.5">
            <div className="p-2 bg-amber-100 text-amber-800 rounded-xl shrink-0 mt-0.5">
              <ShieldAlert className="w-4 h-4" />
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-bold text-amber-900">Security Architecture Constraint:</span>
                <span className="px-2 py-0.5 bg-amber-200/80 text-amber-900 rounded-md font-mono text-[10px] font-bold">
                  Super Admin ≠ Database Superuser
                </span>
              </div>
              <p className="text-amber-800/90 leading-relaxed font-medium">
                Control Center roles enforce control-plane and application gateway policies. Super Admin grants full UI and API orchestrator access, but does <strong className="font-bold">NOT</strong> provide raw database superuser or direct encryption key access. High-risk privilege modifications strictly require hardware MFA step-up and dual-custody audit logging.
              </p>
            </div>
          </div>

          {/* =============================================================== */}
          {/* 3. SIDE-BY-SIDE LAYOUT: DEFINED ROLES + PERMISSIONS DETAIL       */}
          {/* =============================================================== */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* ------------------------------------------------------------- */}
            {/* LEFT COLUMN: DEFINED ROLES TABLE (7 Cols in 12-grid)          */}
            {/* ------------------------------------------------------------- */}
            <div className="lg:col-span-7 bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-[#F1F5F9] flex items-center justify-between bg-white">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-[#0F172A] tracking-wider uppercase">
                    DEFINED ROLES
                  </span>
                  <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] rounded-full text-[11px] font-bold">
                    {filteredRoles.length} Roles Total
                  </span>
                </div>
                <span className="text-[11px] text-[#94A3B8] font-medium">
                  Click a role to inspect permissions
                </span>
              </div>

              {/* Roles Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-[#F1F5F9] text-[#64748B] font-bold text-[11px] bg-[#F8FAFC]/50">
                      <th className="py-3 px-4">Role Name</th>
                      <th className="py-3 px-4">Description</th>
                      <th className="py-3 px-3 text-center">Users</th>
                      <th className="py-3 px-3 text-center">Status</th>
                      <th className="py-3 px-4 text-right">Last Modified</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {filteredRoles.map((role) => {
                      const isSelected = activeRole.id === role.id;
                      return (
                        <tr
                          key={role.id}
                          onClick={() => {
                            setSelectedRoleId(role.id);
                            setIsEditMode(false);
                          }}
                          className={`group transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-blue-50/60 font-medium'
                              : 'hover:bg-[#F8FAFC]'
                          }`}
                        >
                          {/* Role Name */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2.5">
                              <div
                                className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                                  role.id === 'super_admin'
                                    ? 'bg-purple-100 text-purple-700'
                                    : role.type === 'Administrative'
                                    ? 'bg-blue-100 text-[#2563EB]'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {role.id === 'super_admin' ? (
                                  <Shield className="w-3.5 h-3.5" />
                                ) : (
                                  <KeyRound className="w-3.5 h-3.5" />
                                )}
                              </div>
                              <div>
                                <span className={`font-bold block ${isSelected ? 'text-[#2563EB]' : 'text-[#0F172A]'}`}>
                                  {role.name}
                                </span>
                                <span className="text-[10px] text-[#94A3B8] font-mono block">
                                  {role.slug}
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Description */}
                          <td className="py-3.5 px-4 text-[#64748B] max-w-[220px]">
                            <p className="truncate" title={role.description}>
                              {role.description}
                            </p>
                          </td>

                          {/* Users Count Badge */}
                          <td className="py-3.5 px-3 text-center">
                            <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#334155] rounded-md font-mono text-[11px] font-bold">
                              {role.usersCount}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="py-3.5 px-3 text-center">
                            {role.status === 'Active' ? (
                              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px] inline-flex items-center gap-1 border border-emerald-200/50">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-bold text-[10px] inline-flex items-center gap-1">
                                <Lock className="w-2.5 h-2.5" />
                                Locked
                              </span>
                            )}
                          </td>

                          {/* Last Modified */}
                          <td className="py-3.5 px-4 text-right text-[#64748B] font-mono text-[11px]">
                            {role.lastModified}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="p-3 bg-[#F8FAFC] border-t border-[#F1F5F9] flex items-center justify-between text-[11px] text-[#64748B]">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  All roles checked against active RBAC token policies
                </span>
                <button
                  onClick={() => triggerToast('Refreshing role bindings from identity gateway...')}
                  className="text-[#2563EB] hover:underline font-bold flex items-center gap-1 cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" />
                  Sync RBAC
                </button>
              </div>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* RIGHT COLUMN: PERMISSIONS DETAIL & MATRIX (5 Cols in 12-grid) */}
            {/* ------------------------------------------------------------- */}
            <div className="lg:col-span-5 bg-white border border-[#E2E8F0] rounded-2xl shadow-2xs overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-[#F1F5F9] flex items-center justify-between bg-white">
                <div className="min-w-0 flex-1 pr-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-black text-[#0F172A] tracking-wider uppercase truncate">
                      PERMISSIONS: {activeRole.name.toUpperCase()}
                    </span>
                    {activeRole.isSystemProtected && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded font-mono text-[9px] font-black uppercase">
                        System Protected
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-[#64748B] block truncate mt-0.5">
                    {activeRole.permissions.length} granted permission scopes
                  </span>
                </div>

                {/* Edit / Save Action */}
                {!isEditMode ? (
                  <button
                    onClick={handleStartEdit}
                    className="px-3 py-1.5 bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#2563EB] font-bold text-xs rounded-xl shadow-2xs flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Edit</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={handleCancelEdit}
                      className="px-2.5 py-1 text-[#64748B] hover:text-[#0F172A] font-bold text-xs rounded-lg transition-colors cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleInitiateSave}
                      className="px-3 py-1.5 bg-[#2563EB] hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <Check className="w-3.5 h-3.5" />
                      <span>Save Changes</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Sub-Tabs (Matrix, Users, Scope, Audit) */}
              <div className="px-4 border-b border-[#F1F5F9] flex items-center gap-4 bg-[#F8FAFC]/50 text-xs">
                <button
                  onClick={() => setActiveDetailTab('matrix')}
                  className={`py-2.5 border-b-2 font-bold transition-all cursor-pointer ${
                    activeDetailTab === 'matrix'
                      ? 'border-[#2563EB] text-[#2563EB]'
                      : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >
                  Permission Matrix
                </button>
                <button
                  onClick={() => setActiveDetailTab('users')}
                  className={`py-2.5 border-b-2 font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    activeDetailTab === 'users'
                      ? 'border-[#2563EB] text-[#2563EB]'
                      : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >
                  <span>Assigned Users</span>
                  <span className="px-1.5 py-0.2 bg-[#E2E8F0] text-[#475569] rounded-full text-[10px]">
                    {activeRole.assignedUsers.length}
                  </span>
                </button>
                <button
                  onClick={() => setActiveDetailTab('scope')}
                  className={`py-2.5 border-b-2 font-bold transition-all cursor-pointer ${
                    activeDetailTab === 'scope'
                      ? 'border-[#2563EB] text-[#2563EB]'
                      : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >
                  Scope & Policy
                </button>
                <button
                  onClick={() => setActiveDetailTab('audit')}
                  className={`py-2.5 border-b-2 font-bold transition-all cursor-pointer ${
                    activeDetailTab === 'audit'
                      ? 'border-[#2563EB] text-[#2563EB]'
                      : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
                  }`}
                >
                  Audit History
                </button>
              </div>

              {/* Edit Mode Alert / Warning */}
              {isEditMode && (
                <div className="p-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between text-xs text-blue-900">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-3.5 h-3.5 text-[#2563EB]" />
                    <span className="font-bold">Edit Mode Active:</span>
                    <span>Toggle checkboxes to adjust scopes.</span>
                  </div>
                  {highRiskChanges.hasHighRisk && (
                    <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-bold text-[10px] flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      High Risk Changes
                    </span>
                  )}
                </div>
              )}

              {/* Tab Content Area */}
              <div className="p-4 overflow-y-auto max-h-[600px] space-y-4">
                {/* --------------------------------------------------------- */}
                {/* TAB 1: PERMISSION MATRIX                                  */}
                {/* --------------------------------------------------------- */}
                {activeDetailTab === 'matrix' && (
                  <div className="space-y-4">
                    {PERMISSION_CATALOG.map((cat) => {
                      const Icon = cat.icon;
                      const activePermsInCat = cat.permissions.filter((p) =>
                        isEditMode ? stagedPermissions.includes(p.key) : activeRole.permissions.includes(p.key)
                      );
                      const isEntireGroupGranted = activePermsInCat.length === cat.permissions.length;

                      return (
                        <div
                          key={cat.namespace}
                          className="bg-white border border-[#E2E8F0] rounded-xl p-3.5 shadow-2xs space-y-2.5 hover:border-[#CBD5E1] transition-all"
                        >
                          {/* Category Header */}
                          <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-2">
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4 text-[#2563EB]" />
                              <span className="text-xs font-bold text-[#0F172A]">
                                {cat.label}
                              </span>
                            </div>
                            <span className="text-[10px] font-mono text-[#64748B]">
                              {activePermsInCat.length}/{cat.permissions.length} granted
                            </span>
                          </div>

                          {/* Permission Items */}
                          <div className="space-y-2 pt-1">
                            {cat.permissions.map((perm) => {
                              const isChecked = isEditMode
                                ? stagedPermissions.includes(perm.key)
                                : activeRole.permissions.includes(perm.key);

                              return (
                                <div
                                  key={perm.key}
                                  onClick={() => handleTogglePermission(perm.key)}
                                  className={`flex items-start gap-2.5 p-2 rounded-lg transition-all ${
                                    isEditMode ? 'cursor-pointer hover:bg-[#F8FAFC]' : ''
                                  } ${isChecked ? 'bg-slate-50/50' : 'opacity-60'}`}
                                >
                                  {/* Checkbox */}
                                  <div
                                    className={`w-4 h-4 rounded mt-0.5 flex items-center justify-center transition-all ${
                                      isChecked
                                        ? 'bg-[#0F172A] text-white'
                                        : 'border border-[#CBD5E1] bg-white'
                                    }`}
                                  >
                                    {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs font-bold text-[#0F172A]">
                                        {perm.name}
                                      </span>
                                      {perm.isHighRisk && (
                                        <span className="px-1.5 py-0.2 bg-red-100 text-red-700 rounded text-[9px] font-bold uppercase">
                                          High Risk
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-[#64748B] font-medium leading-normal mt-0.5">
                                      {perm.description}
                                    </p>
                                    <span className="text-[9px] font-mono text-[#94A3B8] block mt-0.5">
                                      {perm.key}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* --------------------------------------------------------- */}
                {/* TAB 2: ASSIGNED USERS                                     */}
                {/* --------------------------------------------------------- */}
                {activeDetailTab === 'users' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-xs pb-1">
                      <span className="font-bold text-[#0F172A]">
                        Staff Members Assigned ({activeRole.assignedUsers.length})
                      </span>
                      <button
                        onClick={() => triggerToast('To assign staff, use Users & Accounts management or submit an identity request.')}
                        className="text-[#2563EB] hover:underline font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        Assign Member
                      </button>
                    </div>

                    {activeRole.assignedUsers.length === 0 ? (
                      <div className="text-center py-8 bg-[#F8FAFC] rounded-xl border border-dashed border-[#E2E8F0]">
                        <Users className="w-6 h-6 text-[#94A3B8] mx-auto mb-2" />
                        <p className="text-xs font-bold text-[#64748B]">No users directly assigned</p>
                        <p className="text-[11px] text-[#94A3B8] mt-1">This role is currently vacant.</p>
                      </div>
                    ) : (
                      activeRole.assignedUsers.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-[#0F172A] text-white font-bold text-xs flex items-center justify-center">
                              {u.avatarInitials}
                            </div>
                            <div>
                              <span className="font-bold text-[#0F172A] block">{u.name}</span>
                              <span className="text-[10px] text-[#64748B] font-mono block">{u.email}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full font-bold text-[10px] inline-flex items-center gap-1 border border-emerald-200">
                              <CheckCircle2 className="w-2.5 h-2.5" />
                              MFA Active
                            </span>
                            <span className="text-[10px] text-[#94A3B8] block mt-0.5">
                              Assigned {u.assignedDate}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* --------------------------------------------------------- */}
                {/* TAB 3: SCOPE & POLICY                                     */}
                {/* --------------------------------------------------------- */}
                {activeDetailTab === 'scope' && (
                  <div className="space-y-4 text-xs">
                    <div className="bg-[#F8FAFC] p-4 rounded-xl border border-[#E2E8F0] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[#0F172A]">Enforcement Scope:</span>
                        <span className="px-2 py-0.5 bg-blue-100 text-[#2563EB] rounded font-mono font-bold text-[11px]">
                          {activeRole.scope}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[#0F172A]">Session Timeout Limit:</span>
                        <span className="font-mono text-[#64748B]">60 minutes idle / 8 hours max</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[#0F172A]">Required MFA Strength:</span>
                        <span className="font-bold text-emerald-700">FIDO2 WebAuthn or TOTP</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-[#0F172A]">Server Enforcement:</span>
                        <span className="text-emerald-700 font-bold flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" /> Active (Gateway Middleware)
                        </span>
                      </div>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-xl border border-[#E2E8F0] space-y-2">
                      <h4 className="font-bold text-[#0F172A] flex items-center gap-1.5">
                        <Info className="w-4 h-4 text-[#2563EB]" />
                        Zero-Trust Policy Summary
                      </h4>
                      <p className="text-[#64748B] leading-relaxed">
                        Every control center API request evaluates the calling operator&apos;s signed JWT against the backend RBAC middleware. Scopes are verified dynamically per endpoint without client-side bypass vulnerabilities.
                      </p>
                    </div>
                  </div>
                )}

                {/* --------------------------------------------------------- */}
                {/* TAB 4: AUDIT HISTORY                                      */}
                {/* --------------------------------------------------------- */}
                {activeDetailTab === 'audit' && (
                  <div className="space-y-3 text-xs">
                    {activeRole.auditHistory.length === 0 ? (
                      <p className="text-center py-8 text-[#94A3B8]">No modifications recorded yet.</p>
                    ) : (
                      activeRole.auditHistory.map((log) => (
                        <div
                          key={log.id}
                          className="p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl space-y-1.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-[#0F172A]">{log.action}</span>
                            <span className="text-[10px] font-mono text-[#94A3B8]">{log.timestamp}</span>
                          </div>
                          <p className="text-[11px] text-[#64748B]">{log.details}</p>
                          <div className="flex items-center justify-between text-[10px] text-[#94A3B8] pt-1 border-t border-[#F1F5F9]">
                            <span>Actor: {log.actor} ({log.actorEmail})</span>
                            <span className="font-mono">{log.stepUpMethod}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Detail Footer Note (Matches Stitch Screenshot) */}
              <div className="p-3 bg-[#F8FAFC] border-t border-[#F1F5F9] text-[11px] text-[#64748B] font-medium text-center">
                Role is currently Active. Locked roles cannot be edited without breakglass SRE authorization.
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* =============================================================== */}
      {/* 4. HIGH-RISK STEP-UP AUTH MODAL                                 */}
      {/* =============================================================== */}
      {showStepUpModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-md w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-red-100 text-red-700 rounded-xl">
                  <Fingerprint className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">Step-Up Authentication Required</h3>
                  <span className="text-xs text-[#64748B]">High-Risk Role Modification</span>
                </div>
              </div>
              <button
                onClick={() => setShowStepUpModal(false)}
                className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Impact Preview Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 space-y-2 text-xs">
              <div className="flex items-center gap-2 font-bold text-amber-900">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                <span>Impact Preview:</span>
              </div>
              <p className="text-amber-800">
                Modifying role <strong className="font-bold text-amber-950">&quot;{activeRole.name}&quot;</strong> will affect <strong className="font-bold">{activeRole.usersCount} active staff members</strong>.
              </p>
              {highRiskChanges.addedItems.length > 0 && (
                <div className="text-[11px] text-amber-900">
                  <span className="font-bold text-emerald-800">New High-Risk Privileges: </span>
                  {highRiskChanges.addedItems.map((p) => p.name).join(', ')}
                </div>
              )}
            </div>

            <form onSubmit={handleConfirmStepUp} className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-[#334155] mb-1">
                  Admin Password or FIDO2 Security Pin
                </label>
                <input
                  type="password"
                  value={stepUpPassword}
                  onChange={(e) => setStepUpPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#334155] mb-1">
                  6-Digit Authenticator TOTP Code
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={stepUpTotp}
                  onChange={(e) => setStepUpTotp(e.target.value)}
                  placeholder="123456"
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs font-mono text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#334155] mb-1">
                  Change Rationale (Immutable Audit Log Entry)
                </label>
                <textarea
                  rows={2}
                  value={stepUpReason}
                  onChange={(e) => setStepUpReason(e.target.value)}
                  placeholder="e.g. Q3 Security policy reclassification for Tier 2 support..."
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none resize-none"
                  required
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => setShowStepUpModal(false)}
                  className="px-4 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#64748B] font-bold text-xs rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingAuth}
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isSubmittingAuth ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                      <span>Authorize & Apply</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =============================================================== */}
      {/* 5. CREATE NEW ROLE MODAL                                        */}
      {/* =============================================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-[#F1F5F9] pb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 text-[#2563EB] rounded-xl">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#0F172A]">Create New Role</h3>
                  <span className="text-xs text-[#64748B]">Define custom RBAC permission profile</span>
                </div>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateRole} className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-[#334155] mb-1">
                  Role Display Name *
                </label>
                <input
                  type="text"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g. Senior FX Settlement Analyst"
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#334155] mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                  placeholder="Detailed explanation of staff access responsibilities..."
                  className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#334155] mb-1">
                    Scope Boundary
                  </label>
                  <select
                    value={newRoleScope}
                    onChange={(e: any) => setNewRoleScope(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none"
                  >
                    <option value="Global Platform">Global Platform</option>
                    <option value="Regional Workspace">Regional Workspace</option>
                    <option value="Domain Limited">Domain Limited</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#334155] mb-1">
                    Clone Permissions From Template
                  </label>
                  <select
                    value={newRoleBaseTemplate}
                    onChange={(e) => setNewRoleBaseTemplate(e.target.value)}
                    className="w-full px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl text-xs text-[#1E293B] focus:bg-white focus:border-[#2563EB] outline-none"
                  >
                    <option value="none">Blank (users.read only)</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.permissions.length} perms)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F1F5F9]">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F8FAFC] text-[#64748B] font-bold text-xs rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F172A] hover:bg-[#1E293B] text-white font-bold text-xs rounded-xl shadow-xs flex items-center gap-2 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5 text-white" />
                  <span>Create Role</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
